import { find, flatMap, isEmpty, map } from "lodash";
import { useRouter } from "next/router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import { useCompanies } from "../../../../common/api/useCompanies";
import { useLocations } from "../../../../common/api/useLocations";
import usePermissions from "../../../../common/api/usePermissions";
import { useTrackEvent } from "../../../../common/api/useTrackEvent";
import { IconBox, MessageType, Modal } from "../../../../common/components";
import { API_REST_METHODS } from "../../../../common/constants";
import { ANALYTICS_EVENTS } from "../../../../common/constants/analytics.constants";
import { fetcher } from "../../../../common/hooks";
import { Item, Location, LocationPermissions } from "../../../../common/models";
import { showToast } from "../../../../common/services/toast";
import { useSpaces } from "../../../spaces/api/useSpaces";
import { FLOW_TYPES, LOCATION_TYPES_WITH_ICONS, STEPS } from "../../constants";
import { useSetUpSpacesStore } from "../../store";
import { AddressMask } from "../AddressMask";
import { DialogStepper } from "../DialogStepper";
import { SpacePurposesTable } from "../SpacePurposesTable";

const MAX_ATTEMPTS = 5;
const DELAY = 1000; // 1s
interface SummaryProps {
    existingCompany?: Item;
    existingLocation?: Location | LocationPermissions;
    onAddSpaces?: () => void;
}

const Summary = (props: SummaryProps) => {
    const { existingCompany, existingLocation, onAddSpaces = () => {} } = props;

    const {
        company,
        location,
        locationType,
        address,
        locationTimeZone,
        spacePurposes,
        toggleVisibility,
        setStep,
        reset,
        flowType,
    } = useSetUpSpacesStore();

    const { t } = useTranslation(["common", "setUpSpaces", "spaces", "toast"]);

    const { trackEvent } = useTrackEvent();

    const router = useRouter();
    const { companyId: companyIdFromUrl } = router.query as {
        companyId?: string;
    };
    const companyId = (existingCompany?.id || companyIdFromUrl) as string;

    const [isLoading, setIsLoading] = useState(false);

    const { hasPermissions } = usePermissions();
    const { mutateSpaces } = useSpaces({
        companyId: companyId || undefined,
        locationId: existingLocation?.id,
    });
    const { mutateLocations } = useLocations({ companyId });
    const { companies, mutateCompanies } = useCompanies();
    const companyName = find(companies, { id: companyId })?.name;

    const locationTypeIcon = LOCATION_TYPES_WITH_ICONS.find(
        (locType) => locType.key === locationType
    )?.src;

    const handleFirstSetupSuccess = async () => {
        toggleVisibility();

        if (hasPermissions() && !isEmpty(companies)) {
            mutateCompanies();
            mutateLocations();
            setIsLoading(false);
            reset();
            return;
        }

        // Flow for fetching the first companyId for a user when creating a company
        return new Promise<any>((resolve, reject) => {
            let attempts = 0;

            const checkCompanyId = async () => {
                attempts++;

                const res = await fetcher(
                    "/api/permission-management/v1/users/me/permissions"
                );
                const company = res.companyPermissions[0];
                const companyId = company?.id;
                const locationId = company?.locationPermissions[0]?.id;

                if (companyId && locationId) {
                    resolve({ companyId, locationId });
                } else if (attempts >= MAX_ATTEMPTS) {
                    reject(
                        new Error(`Exceeded maximum attempts: ${MAX_ATTEMPTS}`)
                    );
                } else {
                    setTimeout(checkCompanyId, DELAY);
                }
            };

            checkCompanyId();
        })
            .then(({ companyId, locationId }) =>
                router
                    .push(`/${companyId}/locations/${locationId}/spaces`)
                    .then(() => setIsLoading(false))
            )
            .catch(() => setIsLoading(false))
            .finally(() => reset());
    };

    const handleError = () => {
        setIsLoading(false);
    };

    const handleAddLocationSuccess = () => {
        mutateLocations();
        mutateSpaces();
        setIsLoading(false);
        showToast({
            severity: MessageType.SUCCESS,
            summary: t("toast:locations.created.title"),
            detail: t("toast:locations.created.description"),
        });
        setStep(STEPS.LOCATION_SETUP);
    };

    const handleAddSpacesSuccess = () => {
        mutateSpaces();
        toggleVisibility();
        setIsLoading(false);
        reset();
        onAddSpaces();
    };

    const mappedSpacePurposes = map(spacePurposes, (spacePurpose) => ({
        ...spacePurpose,
        subSpacePurposes: flatMap(spacePurpose.subSpacePurposes, "name"),
    }));

    const { trigger: triggerFirstSetupFlow } = useSWRMutation(
        "/api/backend-for-frontend/v1/firstSetupFlow",
        (url) =>
            fetcher(url, {
                options: {
                    method: API_REST_METHODS.POST,
                    body: JSON.stringify({
                        company,
                        location,
                        locationType,
                        address,
                        locationTimeZone,
                        spacePurposes: mappedSpacePurposes,
                    }),
                },
            }),
        {
            onSuccess: handleFirstSetupSuccess,
            onError: handleError,
        }
    );
    const { trigger: triggerLocationSetupFlow } = useSWRMutation(
        `/api/backend-for-frontend/v1/companies/${companyId}/locationSetupFlow`,
        (url: string) =>
            fetcher(url, {
                options: {
                    method: API_REST_METHODS.POST,
                    body: JSON.stringify({
                        location,
                        locationType,
                        address,
                        locationTimeZone,
                        spacePurposes: mappedSpacePurposes,
                    }),
                },
            }),
        {
            onSuccess: handleAddLocationSuccess,
            onError: handleError,
        }
    );
    const { trigger: triggerAddSpaces } = useSWRMutation(
        `/api/backend-for-frontend/v1/companies/${companyId}/locations/${existingLocation?.id}/addSpaces`,
        (url: string) =>
            fetcher(url, {
                options: {
                    method: API_REST_METHODS.POST,
                    body: JSON.stringify({
                        locationName: existingLocation?.name,
                        spacePurposes,
                    }),
                },
            }),
        {
            onSuccess: handleAddSpacesSuccess,
            onError: handleError,
        }
    );

    const handleClickBack = () => setStep(STEPS.ASSIGN_SPACES);

    const handleFinish = () => {
        setIsLoading(true);
        switch (flowType) {
            case FLOW_TYPES.INITIAL:
                triggerFirstSetupFlow();
                trackEvent(ANALYTICS_EVENTS.FINALIZE_LOCATION_SET_UP);
                break;
            case FLOW_TYPES.ADD_LOCATION:
                triggerLocationSetupFlow();
                trackEvent(ANALYTICS_EVENTS.FINALIZE_LOCATION_SET_UP);
                break;
            case FLOW_TYPES.ADD_SPACES:
                triggerAddSpaces();
                trackEvent(ANALYTICS_EVENTS.ADD_SPACES, {
                    Location: existingLocation?.name,
                    "Spaces' Name": flatMap(
                        flatMap(spacePurposes, "spaces"),
                        "name"
                    ),
                    "Name of Space Purposes": flatMap(
                        flatMap(spacePurposes),
                        "name"
                    ),
                    "Subspaces' Name": flatMap(
                        flatMap(spacePurposes, "subSpacePurposes"),
                        "name"
                    ),
                });
                break;
            default:
                break;
        }
    };

    return (
        <Modal
            isOpen
            title={t("setUpSpaces:summary.title")}
            className="max-w-full"
            hasCancelButton
            hasOkButton
            cancelText={t("common:back") || ""}
            okText={t("common:finish") || ""}
            onClickClose={toggleVisibility}
            onClickCancel={handleClickBack}
            onClickOk={handleFinish}
            isOkLoading={isLoading}
        >
            <div className="flex flex-col gap-4">
                <DialogStepper activeStep={STEPS.SUMMARY} />
                <span>
                    {flowType === FLOW_TYPES.ADD_SPACES ? (
                        <>
                            <Trans
                                i18nKey="spaces:flowSummary"
                                values={{ location: existingLocation?.name }}
                            />
                        </>
                    ) : (
                        <Trans
                            i18nKey="setUpSpaces:summary.intro"
                            values={{
                                location,
                                company: company || companyName,
                            }}
                        />
                    )}
                </span>

                {/* Address details */}
                {flowType !== FLOW_TYPES.ADD_SPACES && (
                    <>
                        <h3 className="text-lg">
                            {t("setUpSpaces:summary.details")}
                        </h3>
                        <div className="flex flex-row gap-4">
                            <div className="text-sm">
                                {t("setUpSpaces:summary.type")}
                            </div>
                            {locationTypeIcon && (
                                <IconBox
                                    src={locationTypeIcon}
                                    label={t(
                                        `setUpSpaces:locationType.${locationType}`
                                    )}
                                />
                            )}
                        </div>
                        <AddressMask
                            address={address}
                            timeZone={locationTimeZone}
                            isReadOnly
                        />
                    </>
                )}

                {/* Spaces details */}
                <h3 className="text-lg">{t("common:spaces")}</h3>
                <SpacePurposesTable isReadOnly />
            </div>
        </Modal>
    );
};

export default Summary;
